import React, { useEffect, useState } from 'react';
import { Button, Flex, Form, Input, message, Space, Select, InputNumber } from 'antd';
import { useRequest, useSetState } from 'ahooks';
import { articleTypeApi, ArticleTypeRes } from '@/axios/articleType';
import CommonTable from '@/components/Table';
import { timeFormatToYearSecond } from '@/utils/date';
import { openDeleteModal } from '@/components/Modal/common';
import EllipsisText from '@/components/Table/EllipsisText';
import SearchInput from '@/components/SearchInput';
import { ColumnsType } from 'antd/es/table';
import CommonModalFactory from '@/plugins/ModalFactory';
import AccessButton from '@/components/AccessButton';

const DEFAULT_SPACE_ID = '7519810157887913999';

const ArticleTypeList: React.FC = () => {
  const [searchParams, setSearchParams] = useSetState({
    pageIndex: 1,
    pageSize: 10,
    typeName: '',
  });

  const {
    data,
    loading,
    runAsync: refreshList,
  } = useRequest(() => articleTypeApi.list(searchParams), {
    refreshDeps: [searchParams],
  });

  const handleDelete = (articleTypeIds: string[]) => {
    openDeleteModal(
      {
        content: `确定删除选中的${articleTypeIds.length}个模板吗？`,
        api: () => articleTypeApi.delete(articleTypeIds as string[]),
      },
      undefined,
      () => {
        setSearchParams({ pageIndex: 1 });
        setSelectedArticleTypeIds([]);
      }
    );
  };

  const columns: ColumnsType<ArticleTypeRes> = [
    {
      title: '文章类型名称',
      dataIndex: 'typeName',
      render: (text: string) => <EllipsisText text={text} />,
    },
    {
      title: '文章名称',
      dataIndex: 'articleName',
      render: (text: string) => <EllipsisText text={text} />,
    },
    {
      title: 'agentId',
      dataIndex: 'agentId',
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      render: (time: string) => timeFormatToYearSecond(time),
    },
    {
      title: '备注',
      dataIndex: 'remark',
      render: (text: string) => <EllipsisText text={text} />,
    },
    {
      title: '操作',
      dataIndex: 'operate',
      render: (_: any, record: ArticleTypeRes) => (
        <Space>
          <AccessButton permission={['article_type:edit']}>
            <Button
              type="link"
              size="small"
              onClick={() => {
                openAddOrEditModal(
                  {
                    detail: record,
                  },
                  {
                    title: '编辑文章类型',
                  },
                  refreshList
                );
              }}
            >
              编辑
            </Button>
          </AccessButton>
          <AccessButton permission={['article_type:delete']}>
            <Button type="link" size="small" danger onClick={() => handleDelete([record.typeId?.toString() || ''])}>
              删除
            </Button>
          </AccessButton>
        </Space>
      ),
    },
  ];

  const [selectedArticleTypeIds, setSelectedArticleTypeIds] = useState<React.Key[]>([]);
  return (
    <div style={{ padding: 24, background: '#fff', height: '100%' }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <AccessButton permission={['article_type:create']}>
          <Button
            type="primary"
            onClick={() => {
              openAddOrEditModal(undefined, undefined, () => {
                setSearchParams({ pageIndex: 1, typeName: '' });
              });
            }}
          >
            新增文章类型
          </Button>
        </AccessButton>
        <Flex gap={8} align="center">
          {selectedArticleTypeIds.length > 0 && (
            <>
              <Button
                onClick={async () => {
                  setSelectedArticleTypeIds([]);
                }}
              >
                取消选择({selectedArticleTypeIds.length})
              </Button>
              <AccessButton permission={['company:article:delete']}>
                <Button
                  type="primary"
                  danger
                  onClick={() => {
                    handleDelete(selectedArticleTypeIds as string[]);
                  }}
                >
                  批量删除({selectedArticleTypeIds.length})
                </Button>
              </AccessButton>
            </>
          )}
        </Flex>
        <SearchInput
          style={{ width: '200px', marginLeft: 'auto' }}
          placeholder="请输入文章类型名称"
          onSearch={(val) => setSearchParams({ typeName: val, pageIndex: 1 })}
        />
      </div>
      <CommonTable
        rowKey="typeId"
        loading={loading}
        columns={columns}
        dataSource={data?.records || []}
        pagination={{
          current: searchParams.pageIndex,
          pageSize: searchParams.pageSize,
          total: data?.total || 0,
          onChange: (page: number, pageSize: number) => setSearchParams({ pageIndex: page, pageSize }),
        }}
        rowSelection={{
          preserveSelectedRowKeys: true,
          selectedRowKeys: selectedArticleTypeIds,
          onChange: (selectedRowKeys) => {
            setSelectedArticleTypeIds(selectedRowKeys);
          },
        }}
      />
    </div>
  );
};

export default ArticleTypeList;
const openAddOrEditModal = CommonModalFactory<{
  detail?: ArticleTypeRes;
}>(
  {
    title: '创建文章类型',
    width: 600,
  },
  ({ data, setOkFn }) => {
    const [form] = Form.useForm();
    useEffect(() => {
      if (data?.detail) {
        form.setFieldsValue({
          ...data.detail,
        });
      } else {
        form.setFieldsValue({
          spaceId: DEFAULT_SPACE_ID,
        });
      }
    }, [data]);
    useEffect(() => {
      setOkFn(async () => {
        const values = await form.validateFields();
        console.log('Submitted values:', values);
        if (data?.detail) {
          await articleTypeApi.save({
            ...data.detail,
            ...values,
          });
          message.success('文章类型更新成功');
        } else {
          await articleTypeApi.create(values);
          message.success('文章类型创建成功');
        }
      });
    }, [form, data]);
    return (
      <Form form={form} labelCol={{ span: 6 }}>
        <Form.Item
          name="typeName"
          label="文章类型名称"
          rules={[
            {
              required: true,
              message: '请输入文章类型名称',
            },
          ]}
        >
          <Input placeholder="请输入文章类型名称" />
        </Form.Item>
        <Form.Item
          name="agentId"
          label="agentId"
          rules={[
            {
              required: true,
              message: '请输入agentId',
            },
          ]}
        >
          <Input placeholder="请输入agentId" />
        </Form.Item>
        <Form.Item name="articleName" label="文章名称">
          <Input placeholder="请输入文章名称" />
        </Form.Item>
        <Form.Item name="spaceId" label="spaceId">
          <Input placeholder="请输入spaceId" />
        </Form.Item>
        <Form.Item name="remark" label="备注">
          <Input placeholder="请输入备注" />
        </Form.Item>
      </Form>
    );
  }
);
